// ── Grenade Inventory ── Manages the local player's grenade stock
// Separate from weapon system to avoid cascading changes across WeaponId consumers.

import {
  type GrenadeId,
  GRENADES,
  MAX_GRENADES_TOTAL,
  MAX_FLASHBANGS,
  GRENADE_OVERHAND_SPEED,
  GRENADE_MEDIUM_SPEED,
  GRENADE_UNDERHAND_SPEED,
} from '../core/config';

/** Maps grenade IDs to the number currently carried. */
export type GrenadeInventory = Record<string, number>;

/** Create an empty grenade inventory (0 of each type). */
export function createEmptyInventory(): GrenadeInventory {
  const inv: GrenadeInventory = {};
  for (const id of Object.keys(GRENADES)) {
    inv[id] = 0;
  }
  return inv;
}

/** Total grenade count across all types. */
export function totalGrenades(inv: GrenadeInventory): number {
  let total = 0;
  for (const id of Object.keys(GRENADES)) {
    total += inv[id] ?? 0;
  }
  return total;
}

/**
 * Can the player carry one more of this grenade type?
 * Checks both per-type max and total carry limit.
 */
export function canCarryGrenade(inv: GrenadeInventory, id: GrenadeId): boolean {
  const cfg = GRENADES[id];
  if (!cfg) return false;

  const currentCount = inv[id] ?? 0;

  // Per-type limit (flashbangs can carry 2, others carry 1)
  if (id === 'flashbang') {
    if (currentCount >= MAX_FLASHBANGS) return false;
  } else {
    if (currentCount >= cfg.maxCarry) return false;
  }

  // Total carry limit
  if (totalGrenades(inv) >= MAX_GRENADES_TOTAL) return false;

  return true;
}

/**
 * Get the throw speed for a given throw strength.
 * @param strength 0 = overhand, 1 = medium, 2 = underhand
 */
export function getThrowSpeed(strength: number): number {
  switch (strength) {
    case 0: return GRENADE_OVERHAND_SPEED;
    case 1: return GRENADE_MEDIUM_SPEED;
    case 2: return GRENADE_UNDERHAND_SPEED;
    default: return GRENADE_OVERHAND_SPEED;
  }
}

/**
 * Get the list of available grenade IDs in inventory (count > 0),
 * useful for cycling through grenades with a key press.
 */
export function getAvailableGrenades(inv: GrenadeInventory): GrenadeId[] {
  const available: GrenadeId[] = [];
  for (const id of Object.keys(GRENADES) as GrenadeId[]) {
    if ((inv[id] ?? 0) > 0) {
      available.push(id);
    }
  }
  return available;
}
